import { useMemo, useState } from "react";
import { useSubs } from "@/context/SubsContext";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { daysUntil, formatDateFR, formatEUR, categoryLabel, toMonthly } from "@/lib/subscriptions";
import { CalendarClock, Bell } from "lucide-react";
import { fr } from "date-fns/locale";

export default function CalendarPage() {
  const { subs, settings, loading } = useSubs();
  const [month, setMonth] = useState(new Date());

  const activeRenewals = useMemo(() => {
    return subs
      .filter((s) => s.status === "actif" && s.next_renewal)
      .map((s) => ({ ...s, date: new Date(s.next_renewal), days: daysUntil(s.next_renewal) }))
      .sort((a, b) => a.date - b.date);
  }, [subs]);

  const renewalDates = useMemo(
    () => activeRenewals.map((s) => new Date(s.next_renewal)),
    [activeRenewals]
  );

  const upcomingThisMonth = useMemo(() => {
    return activeRenewals.filter(
      (s) =>
        s.date.getMonth() === month.getMonth() &&
        s.date.getFullYear() === month.getFullYear()
    );
  }, [activeRenewals, month]);

  const notifyDays = settings?.notify_days_before ?? 3;

  if (loading) {
    return <div className="text-gray-500 py-10 text-center">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="calendar-root">
      <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 flex items-start gap-3 text-sm text-emerald-900">
        <Bell size={16} className="mt-0.5" />
        <div>
          Vous serez prévenu <strong>{notifyDays} jour{notifyDays > 1 ? "s" : ""}</strong> avant chaque renouvellement.
          Les notifications réelles nécessitent une configuration technique supplémentaire (service de notifications push ou e-mail).
          SubSave ne résilie jamais un abonnement à votre place.
        </div>
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="calendar-widget">
          <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Calendrier des renouvellements</h3>
          <Calendar
            mode="multiple"
            selected={renewalDates}
            month={month}
            onMonthChange={setMonth}
            locale={fr}
            modifiersClassNames={{
              selected: "bg-emerald-600 text-white hover:bg-emerald-700 focus:bg-emerald-700",
              today: "ring-1 ring-emerald-400",
            }}
            className="rounded-xl border border-gray-100"
          />
          <div className="flex items-center gap-3 mt-4 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
              Date de renouvellement
            </span>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="month-renewals">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-gray-900">Ce mois-ci</h3>
            <CalendarClock size={18} className="text-emerald-500" />
          </div>
          {upcomingThisMonth.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">Aucun renouvellement ce mois-ci.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {upcomingThisMonth.map((s) => (
                <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{s.name}</div>
                    <div className="text-xs text-gray-500">
                      {formatDateFR(s.next_renewal)} · {formatEUR(toMonthly(s.price, s.frequency))}/mois
                    </div>
                  </div>
                  {Number.isFinite(s.days) && (
                    <Badge
                      className={`rounded-full ${
                        s.days < 0
                          ? "bg-gray-100 text-gray-500"
                          : s.days <= 3
                          ? "bg-red-50 text-red-700"
                          : s.days <= 7
                          ? "bg-amber-50 text-amber-700"
                          : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {s.days < 0 ? "Passé" : `J-${s.days}`}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-2xl bg-white p-6 border border-gray-100 shadow-soft" data-testid="all-renewals">
        <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Tous les prochains renouvellements</h3>
        {activeRenewals.length === 0 ? (
          <div className="text-sm text-gray-400 py-6 text-center">Aucun renouvellement à venir.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeRenewals.map((s) => (
              <li key={s.id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div>
                  <div className="font-semibold text-gray-900">{s.name}</div>
                  <div className="text-xs text-gray-500">{categoryLabel(s.category)} · {formatDateFR(s.next_renewal)}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900 text-sm">{formatEUR(toMonthly(s.price, s.frequency))}</div>
                  <div className="text-[11px] text-gray-500">par mois</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
